import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "common/locales";
import AssignedToDropdown from "features/fieldEdit/AssignedToDropdown";
import {
  Field,
  getCurrentFieldWithAnimalsFromArray,
  useCurrentFarmAnimalGroups,
  useFields,
  useLatestManualReadingDate,
  useLatestMoveInDate,
} from "model";
import { useAnimalGroup } from "model/useAnimalGroupQueries";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const BELLFLOWER_COLOR = "#D3C9E7";

interface LivestockSectionProps {
  field: Field;
  onGroupChange: (guid?: string) => void;
  canMoveIn?: boolean;
  onMoveIn?: () => Promise<void>;
  animalGroupColor?: string | null;
}

interface MetricItemProps {
  label: string;
  value: string | number;
  unit?: string;
  warning?: boolean;
  action?: React.ReactNode;
}

function MetricItem({ label, value, unit, warning, action }: MetricItemProps) {
  return (
    <Box
      sx={{
        py: 0.75,
        borderBottom: "1px solid #F0F0F0",
        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", display: "block", fontSize: "0.7rem" }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: warning ? "#DC2626" : "#1a1a2e",
            fontStyle: warning ? "italic" : "normal",
          }}
        >
          {value}
        </Typography>
        {unit && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.7rem" }}
          >
            {unit}
          </Typography>
        )}
        {action}
      </Box>
    </Box>
  );
}

interface WarningItemProps {
  label: string;
  value?: string;
  action?: React.ReactNode;
}

function WarningItem({ label, value, action }: WarningItemProps) {
  return (
    <Box
      sx={{
        py: 0.75,
        px: 1,
        mx: -1,
        borderBottom: "1px solid #F7E880",
        backgroundColor: "#FDFBEB",
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "#A48137", display: "block", fontSize: "0.7rem" }}
      >
        {label}
      </Typography>
      {value && (
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#A48137" }}>
          {value}
        </Typography>
      )}
      {action}
    </Box>
  );
}

function MetricsCard({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        px: 1.5,
        py: 0.5,
        border: `1px solid ${BELLFLOWER_COLOR}`,
      }}
    >
      {children}
    </Box>
  );
}

const TooltipChild = styled("div")({
  width: "100%",
});

export default function LivestockSection({
  field,
  onGroupChange,
  canMoveIn,
  onMoveIn,
  animalGroupColor,
}: LivestockSectionProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [isSendingMoveRequest, setSendingMoveRequest] = useState(false);

  // Use React Query hooks for animal groups
  const { data: animalGroups } = useCurrentFarmAnimalGroups();
  const { data: currentAnimalGroup } = useAnimalGroup(
    field.animalGroup || undefined
  );

  // Use React Query for fields data
  const { data: allFields = [] } = useFields();

  // Get the field where the animal group is currently located
  const currentFieldWithAnimals = useMemo(
    () => getCurrentFieldWithAnimalsFromArray(allFields, field.animalGroup),
    [allFields, field.animalGroup]
  );

  // Get the last grazing date for this field
  const fieldGuids = useMemo(() => [field.guid], [field.guid]);
  const lastGrazedDate = useLatestManualReadingDate(fieldGuids);

  // Get the move-in date for this field (when animals arrived)
  const moveInDate = useLatestMoveInDate(fieldGuids);

  // Calculate days since last grazed
  const daysSinceLastGrazed = lastGrazedDate
    ? Math.floor(
        (new Date().getTime() - lastGrazedDate.getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : null;

  // Check if animals are currently grazing on this field
  const isCurrentlyGrazing = currentFieldWithAnimals?.guid === field.guid;

  // Calculate days since animals moved in (for currently grazing)
  const daysSinceMoveIn = moveInDate
    ? Math.floor(
        (new Date().getTime() - moveInDate.getTime()) / (1000 * 60 * 60 * 24)
      )
    : null;

  const handleAssignNewGroup = () => {
    setShowAssignDialog(true);
  };

  const handleAnimalGroupClick = () => {
    if (currentAnimalGroup?.guid) {
      navigate(`/animal-group/${currentAnimalGroup.guid}`);
    }
  };

  const handleGroupChange = (guid?: string) => {
    setShowAssignDialog(false);
    onGroupChange(guid);
  };

  const handleMoveIn = async () => {
    if (!onMoveIn || isSendingMoveRequest) return;
    setSendingMoveRequest(true);
    await onMoveIn();
    setSendingMoveRequest(false);
  };

  if (animalGroups.length === 0) return null;

  const moveDisabled = isSendingMoveRequest || !canMoveIn;

  const MoveButton = (
    <Button
      variant="outlined"
      endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
      onClick={handleMoveIn}
      disabled={moveDisabled}
      sx={{
        height: 36,
        fontSize: "14px",
        fontWeight: 500,
        textTransform: "none",
        borderColor: "#2E7831",
        color: "#2E7831",
        borderRadius: "8px",
        px: 2,
        mt: 1,
        "&:hover": {
          borderColor: "#1C4321",
          backgroundColor: "#F7FCF1",
        },
        "&.Mui-disabled": {
          borderColor: "#E0E0E0",
          color: "#9E9E9E",
        },
      }}
    >
      {t("fieldview.livestock.moveHere")}
    </Button>
  );

  return (
    <Box sx={{ mb: 2 }}>
      <MetricsCard>
        {/* Last Grazed */}
        <MetricItem
          label={t("fieldview.livestock.lastGrazed")}
          value={
            isCurrentlyGrazing
              ? daysSinceMoveIn === 0
                ? t("fieldview.livestock.currentlyGrazingSinceToday")
                : daysSinceMoveIn === 1
                ? t("fieldview.livestock.currentlyGrazingSince1Day")
                : daysSinceMoveIn !== null
                ? t("fieldview.livestock.currentlyGrazingSinceDays", {
                    days: daysSinceMoveIn,
                  })
                : t("fieldview.livestock.currentlyGrazing")
              : daysSinceLastGrazed === 0
              ? t("fieldview.livestock.today")
              : daysSinceLastGrazed !== null
              ? t("fieldview.livestock.daysAgo", { days: daysSinceLastGrazed })
              : t("fieldview.livestock.never")
          }
        />

        {/* Currently Assigned Animal Group */}
        {currentAnimalGroup ? (
          <Box
            sx={{
              py: 0.75,
              borderBottom: "1px solid #F0F0F0",
              "&:last-child": {
                borderBottom: "none",
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                fontSize: "0.7rem",
              }}
            >
              {t("fieldview.livestock.assignedGroup")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {/* Colored dot indicator */}
              {animalGroupColor && (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: animalGroupColor,
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#1a1a2e",
                }}
              >
                {currentAnimalGroup.name}
              </Typography>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAssignNewGroup();
                }}
                sx={{
                  color: "#7D5DA3",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  padding: "2px 8px",
                  minWidth: "auto",
                  minHeight: "auto",
                  ml: 1,
                  backgroundColor: "rgba(125, 93, 163, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(125, 93, 163, 0.2)",
                  },
                }}
              >
                {t("fieldview.livestock.change")}
              </Button>
            </Box>
          </Box>
        ) : (
          <WarningItem
            label={t("fieldview.livestock.assignedGroup")}
            action={
              <Button
                size="small"
                onClick={handleAssignNewGroup}
                sx={{
                  color: "#A48137",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  padding: "2px 8px",
                  minWidth: "auto",
                  minHeight: "auto",
                  backgroundColor: "rgba(164, 129, 55, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(164, 129, 55, 0.2)",
                  },
                }}
              >
                {t("fieldview.livestock.assign")}
              </Button>
            }
          />
        )}

        {/* Current Location of Animal Group */}
        {currentAnimalGroup && (
          <>
            {currentFieldWithAnimals ? (
              <MetricItem
                label={t("fieldview.livestock.currentLocation")}
                value={currentFieldWithAnimals.name}
              />
            ) : (
              <WarningItem
                label={t("fieldview.livestock.currentLocation")}
                value={t("fieldview.livestock.notSet")}
              />
            )}
          </>
        )}
      </MetricsCard>

      {/* Move Animals Here Button */}
      {currentAnimalGroup && onMoveIn && (
        <Box sx={{ mt: 1.5 }}>
          {canMoveIn ? (
            MoveButton
          ) : (
            <Tooltip
              title={
                field.animalGroup
                  ? t("fieldlistmenu.moveIn.tooltip.sameField")
                  : t("fieldlistmenu.moveIn.tooltip.silage")
              }
            >
              <TooltipChild>{MoveButton}</TooltipChild>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Assign Animal Group Dialog */}
      <Dialog
        open={showAssignDialog}
        onClose={() => setShowAssignDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("fieldview.livestock.assignDialogTitle")}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <AssignedToDropdown
              assignedTo={field.animalGroup}
              onChange={handleGroupChange}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
